import { useMemo } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import { useDealOperations } from '@/hooks/deals/useDealOperations';
import { filterByOrg } from '@/mockData/common/demoFilters';
import { ALL_DEALS } from '@/mockData';
import { ARI_SUGGESTED_DEALS } from '@/mockData/ariPipeline';
import type { Deal, DealAnalysisState, PipelineStage } from 'src/types/deal.types';
import type { DealWithTitle } from 'src/types/deal.types';
import { DealStage } from '@equaliq/api-model';

// Map api-model DealStage → frontend PipelineStage. The kanban renders one of
// two column sets depending on the variant:
//   • Standard (PIPELINE_COLUMNS): lead/in_progress/signing/delivery/complete
//   • Ari      (ARI_PIPELINE_COLUMNS): lead/in_progress/won/lost/closed
// A deal's `stage` MUST be a column id in whichever set is rendered, or the
// kanban drops it (no matching column → invisible card). The real /deal-pipeline
// route now renders the Ari variant (rich header), so real deals have to speak
// Ari's column language — hence the `ari` switch below. Cancelled deals fall
// through to in_progress so they stay visible until the user moves them.
function apiStageToPipelineStage(
  stage: string | undefined,
  variant: 'standard' | 'ari',
): PipelineStage {
  if (variant === 'ari') {
    // Ari columns have no signing/delivery lane. The backend's terminal stages
    // (signing/delivery/completed) collapse onto Ari's terminal "closed";
    // won/lost are reachable from there via the card's stage-transition buttons.
    switch (stage) {
      case DealStage.signing:
      case DealStage.delivery:
      case DealStage.completed:
        return 'closed';
      case DealStage.drafting:
      case DealStage.negotiation:
      case DealStage.cancelled:
      default:
        return 'in_progress';
    }
  }
  switch (stage) {
    case DealStage.signing: return 'signing';
    case DealStage.delivery: return 'delivery';
    case DealStage.completed: return 'complete';
    case DealStage.drafting:
    case DealStage.negotiation:
    case DealStage.cancelled:
    default:
      return 'in_progress';
  }
}

// "Untitled Deal" is what NewDealPage stamps on creation when the user
// doesn't fill a title. Treat both that literal and any blank/whitespace
// title as missing so the filename fallback kicks in.
const isPlaceholderTitle = (t: string | undefined): boolean => {
  if (!t) return true;
  const trimmed = t.trim();
  return trimmed.length === 0 || trimmed.toLowerCase() === 'untitled deal';
};

const stripFileExt = (name: string): string => name.replace(/\.[^./]+$/, '');

function apiAnalysisStatusToState(status: string | undefined, hasFile: boolean): DealAnalysisState {
  if (!status) return hasFile ? 'pending' : 'none';
  const s = status.toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'success') return 'complete';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'running' || s === 'in_progress' || s === 'processing') return 'running';
  // Thread-only deals (Phase 2 categorizer create_new without a PDF
  // attachment) carry status='pending' from the backend because the
  // analysis row exists but the engine can't run without a file. Surface
  // those as 'none' ("No file") so the kanban doesn't show a perpetually
  // spinning "Pending" pill on every email-sourced deal.
  if (s === 'pending' && !hasFile) return 'none';
  return 'pending';
}

function apiDealToKanbanDeal(deal: DealWithTitle, variant: 'standard' | 'ari'): Deal {
  const docs = deal.documentsCount ?? 0;
  const displayName = isPlaceholderTitle(deal.title)
    ? deal.firstFileName
      ? stripFileExt(deal.firstFileName)
      : deal.title || `Deal ${deal.dealId.slice(0, 8)}`
    : deal.title;
  return {
    id: deal.dealId,
    name: displayName,
    type: 'Contract',
    dealType: 'music',
    stage: apiStageToPipelineStage(deal.latestStage, variant),
    value: deal.dealValueAmount ?? 0,
    date: deal.createdAt,
    progress: 0,
    documentsCount: docs,
    collaborators: [],
    lastActivity: deal.updatedAt,
    dueDate: null,
    description: deal.description ?? '',
    analysisState: apiAnalysisStatusToState(deal.latestAnalysisStatus, docs > 0),
    primaryContractId: deal.primaryContractId,
    sourceType: deal.sourceType,
    linkedThreadCount: deal.linkedThreadCount,
    participantCount: deal.participantCount,
  };
}

export function useDealPipelineData() {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();
  // In prod, fetch real deals from the backend so engine-born deals appear
  // in the kanban. (Was hardcoded to [] previously; the prod kanban was
  // permanently empty.) Demo mode still uses the canned mock dataset.
  // Surfacing deleteDeal so the kanban card's "Delete" menu can wire up
  // to a real mutation — the SWR cache key inside this hook is what the
  // kanban renders from, so the deletion + refetch needs to happen on
  // this same hook instance to update the UI without a manual reload.
  const { deals: apiDeals, deleteDeal } = useDealOperations(
    isDemo ? undefined : selectedOrgId,
  );

  // Both demo and the real route render the rich Ari pipeline variant (the
  // CEO's mydealflow.app look). On the real route the deals are live; on demo
  // they're the canned dataset. Real deals are mapped to Ari's column ids so
  // none vanish (see apiStageToPipelineStage 'ari' branch).
  const isAriPipeline = true;

  const initialDeals = useMemo(
    () =>
      isDemo
        ? filterByOrg(ALL_DEALS, selectedOrgId)
        : apiDeals.map((d) => apiDealToKanbanDeal(d, 'ari')),
    [isDemo, selectedOrgId, apiDeals],
  );

  // The suggested-deals carousel is a demo-only affordance (curated mock deals
  // the user can "add"). There's no real source for it yet, so it's empty on
  // the real route — the carousel hides itself when the list is empty.
  const suggestedDeals = useMemo(
    () => (isDemo ? ARI_SUGGESTED_DEALS : []),
    [isDemo],
  );

  return { initialDeals, suggestedDeals, isAriPipeline, deleteDeal };
}
